/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { Dispatch, SetStateAction, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
// Component imports
import { Modal, Typography } from "antd";
import { IPendingSubscriptionAction } from "@/interfaces/SubscriptionPlans";
// Misc imports
import { useUpdateSubscription } from "@/api/subscriptions";
import { useCancelSubscription } from "@/api/subscriptions/useCancelSubscription";

export const CancelSubscriptionModal = ({
  pendingSubscriptionAction,
  setPendingSubscriptionAction,
}: {
  pendingSubscriptionAction: IPendingSubscriptionAction | null;
  setPendingSubscriptionAction: Dispatch<
    SetStateAction<IPendingSubscriptionAction | null>
  >;
}) => {
  const { workspaceId, projectId } = useParams();
  const { t } = useTranslation();
  const {
    mutate: updateSubscription,
    isLoading: isUpdatingSubscription,
    isSuccess: isUpdateSuccessful,
  } = useUpdateSubscription();
  const {
    mutate: cancelSubscription,
    isLoading: isCancelingSubscription,
    isSuccess: isCancelSuccessful,
  } = useCancelSubscription();

  useEffect(() => {
    if (isUpdateSuccessful || isCancelSuccessful) {
      setPendingSubscriptionAction(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCancelSuccessful, isUpdateSuccessful]);

  const handlePendingAction = () => {
    if (!pendingSubscriptionAction) {
      return;
    }
    if (pendingSubscriptionAction.action === "cancel") {
      cancelSubscription({
        workspaceId: workspaceId as string,
        projectId: projectId as string,
      });
    }
    if (pendingSubscriptionAction.action === "downgrade") {
      updateSubscription({
        workspaceId: workspaceId as string,
        projectId: projectId as string,
        priceId: pendingSubscriptionAction.metadata?.priceId,
      });
    }
  };

  if (!pendingSubscriptionAction) {
    return;
  }

  return (
    <Modal
      title={
        pendingSubscriptionAction.action === "cancel"
          ? t("subscription_plans.cancel_subscription")
          : t("subscription_plans.downgrade_subscription")
      }
      open={!!pendingSubscriptionAction}
      mask
      centered
      onCancel={() => setPendingSubscriptionAction(null)}
      okText={t("common.yes")}
      okButtonProps={{
        loading: isCancelingSubscription || isUpdatingSubscription,
      }}
      cancelText={t("common.no")}
      closable={false}
      onOk={handlePendingAction}
    >
      <Typography.Paragraph>
        {pendingSubscriptionAction.action === "cancel"
          ? t("subscription_plans.cancel_description")
          : t("subscription_plans.downgrade_description")}
      </Typography.Paragraph>
    </Modal>
  );
};
